import type { Store } from 'vuex'
import type { LocaleEnum } from '../../enums'
import type {
  AppConfig,
  CustomizationConfig,
  Dictionary,
  ErrorMessage,
  ErrorsMessages,
} from '../../models'
import Vue from 'vue'
import { useApiError } from '../../composables/use-api-error'
import { CONTAINER } from '../../config/inversify.config'
import { TYPES } from '../../config/inversify.types'
import { DeviceEnum } from '../../enums'
import { capitalizeSentence } from '../../filters/capitalize-sentence.filter'
import { MEDIA_CONSTS } from '../../store/media/media.consts'
import { MESSAGES_CONSTS } from '../../store/messages/messages.consts'
import { compileMessage } from '../messages.utils'
import { NumberUtils } from '../number.utils'

declare module 'vue/types/vue' {
  interface Vue {
    $epb: {
      version: string
      environment: string
      device: DeviceEnum
      isHandheldDevice: boolean
      locale: LocaleEnum
      messages: Dictionary
      customization: CustomizationConfig
      utils: {
        formatNumber: (
          numberValue: number,
          options?: Intl.NumberFormatOptions,
          roundLongNumber?: boolean,
          roundBillions?: boolean,
        ) => string
        compileMessage: (message: string, params: Dictionary<string>) => string
        decodeErrorMessage: (error: any) => ErrorMessage
      }
      useOldWelcome: boolean
      useFeedback: boolean
      showEqualizeDisabledFeatures: boolean
      loadInstancesFromInstanceManagement: boolean
      oidcLogin: boolean
      showExplainFilters: boolean
      showExplainFiltersAside: boolean
      useShoppablePromotions: boolean
      useBulkActions: boolean
      useDefaultDevicesTops: boolean
    }
  }
}

export function initApp<T extends AppConfig>(config: T): void {
  Vue.config.productionTip = false

  CONTAINER.rebind<AppConfig>(TYPES.AppConfig).toConstantValue(config)
  const store = CONTAINER.get<Store<unknown>>(TYPES.Store)
  Vue.prototype.$epb = {
    version: config.version,
    environment: config.environment,
    get device() {
      return store.getters[`${MEDIA_CONSTS.moduleName}/${MEDIA_CONSTS.getters.device}`]
    },
    get isHandheldDevice() {
      return (
        store.getters[`${MEDIA_CONSTS.moduleName}/${MEDIA_CONSTS.getters.device}`] <
        DeviceEnum.SMALL_DESKTOP
      )
    },
    get locale() {
      return store.getters[`${MESSAGES_CONSTS.moduleName}/${MESSAGES_CONSTS.getters.locale}`]
    },
    get messages() {
      return store.getters[`${MESSAGES_CONSTS.moduleName}/${MESSAGES_CONSTS.getters.messages}`]
    },
    customization: config.customization,
    defaultKeepLoggedIn: config.defaultKeepLoggedIn,
    utils: {
      formatNumber: (
        numberValue: number,
        options = {},
        roundLongNumber = false,
        roundBillions = true,
      ) => {
        const locale: string =
          store.getters[`${MESSAGES_CONSTS.moduleName}/${MESSAGES_CONSTS.getters.locale}`]
        return NumberUtils.format(numberValue, locale, options, roundLongNumber, roundBillions)
      },
      compileMessage,
      decodeErrorMessage: (error: any): ErrorMessage => {
        const messages =
          store.getters[`${MESSAGES_CONSTS.moduleName}/${MESSAGES_CONSTS.getters.messages}`]
        const { getErrorMessage, isApiError } = useApiError({
          messages: messages.errors as ErrorsMessages,
        })

        // API error
        if (isApiError(error)) {
          return getErrorMessage(error)
        }

        // Runtime JS error
        // Log the original error to the console for debugging
        console.error(error)
        return {
          title: compileMessage(messages.errors.default.title, { code: '' }),
          message: messages.errors.default.message,
        }
      },
    },
    useOldWelcome: config.useOldWelcome ?? false,
    useFeedback: config.useFeedback ?? false,
    showEqualizeDisabledFeatures: config.showEqualizeDisabledFeatures ?? false,
    loadInstancesFromInstanceManagement: config.loadInstancesFromInstanceManagement ?? false,
    oidcLogin: config.oidcLogin ?? false,
    showExplainFilters: config.showExplainFilters ?? false,
    showExplainFiltersAside: config.showExplainFiltersAside ?? true,
    useShoppablePromotions: config.useShoppablePromotions ?? false,
    useBulkActions: config.useBulkActions ?? false,
    useDefaultDevicesTops: config.useDefaultDevicesTops ?? false,
  }

  Vue.filter('capitalizeSentence', capitalizeSentence)

  window.addEventListener('resize', () => {
    store.dispatch(
      `${MEDIA_CONSTS.moduleName}/${MEDIA_CONSTS.actions.setViewportWidth}`,
      window.innerWidth,
    )
  })

  const faviconUrl = config.customization.logos.favicon
  if (faviconUrl) {
    document.querySelector("link[rel~='icon']")?.setAttribute('href', faviconUrl)
  }

  const themeStorageKey = 'vueuse-color-scheme'
  if (localStorage.getItem(themeStorageKey) === 'system') {
    localStorage.setItem(themeStorageKey, 'auto')
  }
}
